import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TableLink } from '@/components/global';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  emptyRefillListMessage,
  formatRefillWhen,
  refillSessionPath,
  type RefillSessionListItem,
  type RefillSessionListResult,
} from '@/lib/refillSession';
import { RefillSourceBadge, RefillStatusBadge } from './RefillStatusBadge';

interface RefillHistoryCardProps {
  machineId: string;
}

export function RefillHistoryCard({ machineId }: RefillHistoryCardProps) {
  const navigate = useNavigate();
  const [items, setItems] = useState<RefillSessionListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({
          machineId,
          filter: 'all',
          page: '1',
          pageSize: '8',
        });
        const res = await apiFetch(`/api/v1/refill-sessions?${params}`);
        if (res.status === 403) {
          if (!cancelled) {
            setItems([]);
            setError(null);
          }
          return;
        }
        if (!res.ok) {
          throw new Error('Failed to load refill sessions');
        }
        const data: RefillSessionListResult = await res.json();
        if (!cancelled) {
          setItems(data.items ?? []);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load refill sessions');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [machineId]);

  const leftoverSession = items.find((item) => item.leftoverPending);
  const leftoverUnits = leftoverSession?.leftoverUnits ?? 0;

  return (
    <Card>
      <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <CardTitle>Refills</CardTitle>
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(`/refills?machineId=${encodeURIComponent(machineId)}&filter=all`)}
        >
          View all
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {leftoverSession ? (
          <div
            className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950"
            role="status"
          >
            <p>
              {leftoverUnits} unit{leftoverUnits === 1 ? '' : 's'} still need returning to the hub.
            </p>
            <Button size="sm" asChild>
              <Link to={refillSessionPath(leftoverSession.id)}>Return leftovers</Link>
            </Button>
          </div>
        ) : null}

        {error ? <p className="text-sm text-destructive">{error}</p> : null}

        {loading ? (
          <p className="py-6 text-center text-sm text-muted-foreground">Loading refills…</p>
        ) : items.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            {emptyRefillListMessage('all', true)}
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="hidden sm:table-cell">Source</TableHead>
                <TableHead className="text-right">Leftover</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id} className={item.leftoverPending ? 'bg-amber-50' : undefined}>
                  <TableCell>
                    <TableLink to={refillSessionPath(item.id)}>{formatRefillWhen(item)}</TableLink>
                  </TableCell>
                  <TableCell>
                    <RefillStatusBadge status={item.status} />
                  </TableCell>
                  <TableCell className="hidden sm:table-cell">
                    <RefillSourceBadge source={item.completionSource} />
                  </TableCell>
                  <TableCell className={`text-right ${item.leftoverPending ? 'font-semibold text-amber-800' : ''}`}>
                    {item.leftoverPending
                      ? item.leftoverUnits
                      : item.leftoversReturned && item.leftoverUnits > 0
                        ? `${item.leftoverUnits} returned`
                        : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
